import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import Cache from "../utils/cache";

import ProductTile from "../components/ProductTile";
import BankOfferModel from "../components/BankOfferModal";
import { normalizeVendorName, isVendorMatch } from "../utils/vendorMatchUtils";
import queryString from "query-string";

const ITEMS_PER_PAGE = 20;

export default function SpecialOfferPage() {
  const { offerType } = useParams();

  const [offers, setOffers] = useState([]);
  const [, setBankOffers] = useState([]);
  const [banksLogos, setBanksLogos] = useState({});
  const [banksAddresses, setBanksAddresses] = useState({});
  const [matchedBankOfferMap, setMatchedBankOfferMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBankOffer, setSelectedBankOffer] = useState(null);

  // Pagination & sorting state
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState(() => {
    const params = queryString.parse(window.location.search);
    return typeof params.sort === "string" ? params.sort : "newest";
  });

  const initialPageSetRef = useRef(false);
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const prevSortOrderRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Sync page and sortOrder from URL
  useEffect(() => {
    if (initialPageSetRef.current) return;

    const params = queryString.parse(routerLocation.search);
    const pageFromUrl = parseInt(params.page || "1", 10);
    const sortFromUrl = typeof params.sort === "string" ? params.sort : "newest";

    if (!isNaN(pageFromUrl) && pageFromUrl > 0) {
      setCurrentPage(pageFromUrl);
    }

    setSortOrder(sortFromUrl);
    prevSortOrderRef.current = sortFromUrl;
    initialPageSetRef.current = true;
  }, [routerLocation.search]);

  // Update URL when page or sortOrder changes
  useEffect(() => {
    const params = queryString.parse(routerLocation.search);
    let needUpdate = false;

    if (parseInt(params.page || "1", 10) !== currentPage) {
      params.page = currentPage;
      needUpdate = true;
    }
    if ((params.sort || "newest") !== sortOrder) {
      params.sort = sortOrder;
      needUpdate = true;
    }

    if (needUpdate) {
      const newSearch = queryString.stringify(params);
      navigate({ search: newSearch }, { replace: true });
    }
  }, [currentPage, sortOrder, navigate, routerLocation.search]);

  // Load data
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      setError(null);

      try {
        const cachedData = Cache.getData() || {};

        // Load product offers
        let productOffers = cachedData.productOffers || [];
        if (!productOffers.length) {
          const snap = await getDocs(collection(db, "product_offers"));
          let allOffers = [];
          snap.docs.forEach((doc) => {
            const data = doc.data();
            if (Array.isArray(data.offers)) {
              allOffers.push(
                ...data.offers.map((offer) => ({
                  ...offer,
                  id: `${doc.id}_${offer.offerId || Math.random().toString(36).slice(2, 8)}`,
                  uploadedDttm: offer.uploadedDttm || data.uploadedDttm || null,
                }))
              );
            }
          });
          productOffers = allOffers;
          Cache.setData({ ...cachedData, productOffers });
        }

        // Load bank offers
        let banks = cachedData.bankOffers || [];
        if (!banks.length) {
          const snapBanks = await getDocs(collection(db, "bank_offers"));
          let allBanks = [];
          snapBanks.docs.forEach((doc) => {
            const bankName = doc.id;
            const data = doc.data();
            if (Array.isArray(data.offers)) {
              allBanks.push(
                ...data.offers.map((offer, idx) => ({
                  ...offer,
                  bankName,
                  id: `${bankName}_offer_${idx}`,
                }))
              );
            }
          });
          banks = allBanks;
          Cache.setData({ ...Cache.getData(), bankOffers: banks });
        }
        setBankOffers(banks);

        // Load banks meta (logos and addresses)
        let meta = cachedData.banksMeta || {};
        if (!Object.keys(meta).length) {
          const banksSnap = await getDocs(collection(db, "banks"));
          const banksMetaObj = {};
          banksSnap.forEach((doc) => {
            const data = doc.data();
            banksMetaObj[doc.id.toLowerCase()] = {
              logo: data.logo || "",
              address: data.address || "",
            };
          });
          meta = banksMetaObj;
          Cache.setData({ ...Cache.getData(), banksMeta: meta });
        }

        const banksLogoMap = {};
        const banksAddrMap = {};
        Object.entries(meta).forEach(([key, data]) => {
          banksLogoMap[key] = data.logo || "";
          banksAddrMap[key] = data.address || "";
        });
        setBanksLogos(banksLogoMap);
        setBanksAddresses(banksAddrMap);

        // Filter product offers
        const now = new Date();
        const getUTCStartOfDay = (d) =>
          new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
        const nowUTC = getUTCStartOfDay(now);

        const filteredOffers = productOffers.filter((offer) => {
          const startDate = offer.startDttm?.toDate
            ? offer.startDttm.toDate()
            : new Date(offer.startDttm || 0);
          const endDate = offer.endDttm?.toDate
            ? offer.endDttm.toDate()
            : new Date(offer.endDttm || 0);

          const startUTC = getUTCStartOfDay(startDate);
          const endUTC = getUTCStartOfDay(endDate);
          const active = startUTC <= nowUTC && nowUTC <= endUTC;

          const offerTypeMatch =
            (offer.offerType || "").toLowerCase() === (offerType || "").toLowerCase();

          return active && offerTypeMatch;
        });

        // Match bank offers to product offers
        const bankMap = {};
        filteredOffers.forEach((p) => {
          if (!p.vendor) return;
          const pNorm = normalizeVendorName(p.vendor);
          const matches = banks.filter((b) => {
            if (!b.vendor) return false;
            const bNorm = normalizeVendorName(b.vendor);
            return isVendorMatch(bNorm, pNorm);
          });
          if (matches.length > 0) {
            bankMap[p.id] = matches;
          }
        });

        setOffers(filteredOffers);
        setMatchedBankOfferMap(bankMap);
      } catch (e) {
        setError("Failed to load special offers: " + e.message);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [offerType]);

  // Sorting
  const sortedOffers = useMemo(() => {
    if (!offers) return [];

    const getDate = (o) =>
      o.uploadedDttm?.toDate ? o.uploadedDttm.toDate() : new Date(0);

    const getPrice = (o) => {
      const price = parseFloat(o.offerPrice);
      return isNaN(price) ? Infinity : price;
    };

    const getDiscount = (o) => {
      const offerPrice = parseFloat(o.offerPrice);
      const originalPrice = parseFloat(o.originalPrice);

      if (isNaN(offerPrice)) return null;
      if (offerPrice === 0) return 1;
      if (isNaN(originalPrice) || originalPrice <= 0) return null;
      return (originalPrice - offerPrice) / originalPrice;
    };

    const sorted = [...offers].sort((a, b) => {
      switch (sortOrder) {
        case "newest":
          return getDate(b) - getDate(a);
        case "oldest":
          return getDate(a) - getDate(b);
        case "priceLow":
          return getPrice(a) - getPrice(b);
        case "priceHigh":
          return getPrice(b) - getPrice(a);
        case "bestDiscount": {
          const discA = getDiscount(a);
          const discB = getDiscount(b);
          if (discA !== null && discB !== null) {
            if (discB !== discA) return discB - discA;
            return getDate(b) - getDate(a);
          }
          if (discA === null && discB === null) {
            if (getPrice(a) !== getPrice(b)) return getPrice(a) - getPrice(b);
            return getDate(b) - getDate(a);
          }
          if (discA !== null && discB === null) return -1;
          if (discA === null && discB !== null) return 1;
          return 0;
        }
        default:
          return getDate(b) - getDate(a);
      }
    });

    return sorted;
  }, [offers, sortOrder]);

  // Pagination
  const totalPages = Math.ceil(sortedOffers.length / ITEMS_PER_PAGE);
  const paginatedOffers = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedOffers.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedOffers, currentPage]);

  useEffect(() => {
    if (currentPage < 1) {
      setCurrentPage(1);
    } else if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const onSortChange = (newSort) => {
    if (newSort !== sortOrder) {
      setSortOrder(newSort);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loader"></div>
        <style>{`
          .loader {
            border: 6px solid #f3f3f3;
            border-top: 6px solid #3b82f6;
            border-radius: 50%;
            width: 48px;
            height: 48px;
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );

  if (error) return <p className="text-red-600 text-center mt-10">{error}</p>;

  if (!offers.length)
    return (
      <div className="text-center text-gray-600 mt-10">
        <p className="text-4xl mb-2">😞</p>
        <p className="text-lg font-semibold">
          No <b>{offerType}</b> offers available at the moment.
        </p>
        <p>Please check back later.</p>
      </div>
    );

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6 text-center text-indigo-700 drop-shadow-md tracking-wide">
        Special Offer: <span className="text-rose-500">{offerType}</span>
      </h1>

      {/* Sort dropdown */}
      <div className="mb-4 flex justify-end items-center gap-4">
        <label htmlFor="sortOrder" className="font-semibold">
          Sort By:
        </label>
        <select
          id="sortOrder"
          value={sortOrder}
          onChange={(e) => onSortChange(e.target.value)}
          className="border border-gray-300 rounded px-2 py-1"
        >
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="priceLow">Price: Low to High</option>
          <option value="priceHigh">Price: High to Low</option>
          <option value="bestDiscount">Best Discount</option>
        </select>
      </div>

      {/* Offers grid */}
      <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
        {paginatedOffers.map((offer) => (
          <ProductTile
            key={offer.id}
            offer={offer}
            onClick={() =>
              navigate(`/product/${offer.id}?page=${currentPage}&sort=${sortOrder}`)
            }
            matchedBankOffers={matchedBankOfferMap[offer.id]}
            banksLogos={banksLogos}
            setSelectedBankOffer={setSelectedBankOffer}
          />
        ))}
      </div>

      {/* Pagination */}
      <div className="flex justify-center mt-6 space-x-3">
        <button
          className="px-3 py-1 border rounded disabled:opacity-50"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        >
          Previous
        </button>

        <span className="px-3 py-1 border rounded">
          Page {currentPage} of {totalPages}
        </span>

        <button
          className="px-3 py-1 border rounded disabled:opacity-50"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        >
          Next
        </button>
      </div>

      {/* Bank offer modal */}
      {selectedBankOffer && (
        <BankOfferModel
          offer={selectedBankOffer}
          banksLogos={banksLogos}
          banksAddresses={banksAddresses}
          onClose={() => setSelectedBankOffer(null)}
        />
      )}
    </div>
  );
}
